/**
 * Funciones para generar las opciones de configuración de los gráficos de la página de dashboard.
 *
 */
const colorPalette = [
    "#117ACB",
    "#04373D",
    "#068190",
    "#0FBEF9",
    "#B45DFF",
    "#674485",
    "#117ACB",
];

/**
 * Función para obtener un color aleatorio que no haya sido utilizado previamente.
 *
 * @param {array} usedColors
 * @return {array} color
 */
const getRandomColor = (usedColors) => {
    const availableColors = colorPalette.filter(
        (color) => !usedColors.includes(color)
    );
    const randomIndex = Math.floor(Math.random() * availableColors.length);
    return availableColors[randomIndex];
};

/**
 * Función para obtener las opciones de configuración de un gráfico de Toxicidad Aguda - Mortales.
 *
 * @param {object} dataDash2
 * return {object} options
 */
export function getCategoryChartOptions(dataDash2) {
    if (
        !dataDash2 ||
        typeof dataDash2.oralcategory1_count === "undefined" ||
        typeof dataDash2.oralcategory2_count === "undefined" ||
        typeof dataDash2.inhalationcategory1_count === "undefined" ||
        typeof dataDash2.inhalationcategory2_count === "undefined" ||
        typeof dataDash2.cutaneouscategory1_count === "undefined" ||
        typeof dataDash2.cutaneouscategory2_count === "undefined"
    ) {
        return {};
    }

    const categories = ["Ingestión", "Dérmica", "Inhalación"];
    const series2011 = [
        dataDash2.oralcategory1_count,
        dataDash2.cutaneouscategory1_count,
        dataDash2.inhalationcategory1_count,
    ];
    const series2012 = [
        dataDash2.oralcategory2_count,
        dataDash2.cutaneouscategory2_count,
        dataDash2.inhalationcategory2_count,
    ];

    return {
        tooltip: {
            trigger: "axis",
            axisPointer: {
                type: "shadow",
            },
        },
        legend: {
            data: ["Categoría 1", "Categoría 2"],
            textStyle: {
                fontFamily: "Poppins",
                color: "#333",
            },
            bottom: "0",
            left: "center",
            icon: "circle",
        },
        grid: {
            top: "10%",
            left: "3%",
            right: "4%",
            bottom: "10%",
            containLabel: true,
        },
        xAxis: {
            type: "category",
            data: categories,
            axisLabel: {
                fontFamily: "Poppins",
                color: "#333",
                fontSize: 12,
            },
            axisTick: {
                show: false,
            },
            axisLine: {
                show: false,
            },
        },
        yAxis: {
            type: "value",
            splitLine: {
                lineStyle: {
                    type: "dashed",
                    color: "#E0E0E0",
                },
            },
            axisLabel: {
                fontFamily: "Poppins",
                color: "#333",
                fontSize: 12,
            },
            axisTick: {
                show: false,
            },
            axisLine: {
                show: false,
            },
        },
        series: [
            {
                name: "Categoría 1",
                type: "bar",
                data: series2011,
                itemStyle: {
                    color: "#0CB1F7",
                    borderRadius: [15, 15, 0, 0],
                },
                barWidth: "26%",
                barGap: "100%",
            },
            {
                name: "Categoría 2",
                type: "bar",
                data: series2012,
                itemStyle: {
                    color: "#04373D",
                    borderRadius: [15, 15, 0, 0],
                },
                barWidth: "26%",
            },
        ],
    };
}

/**
 * Función para obtener las opciones de configuración de un gráfico de Carcinogenicidad.
 *
 * @param {object} dataDash2
 * return {object} options
 */
export function getCarcinogenicityChartOptions(dataDash2) {
    if (
        !dataDash2 ||
        typeof dataDash2.carcinogenic_group_1_count === "undefined" ||
        typeof dataDash2.carcinogenic_group_2A_count === "undefined" ||
        typeof dataDash2.carcinogenic_group_2B_count === "undefined" ||
        typeof dataDash2.carcinogenic_reported_by_sds_count === "undefined"
    ) {
        return {};
    }

    const series = [
        {
            name: "Grupo 1 - Cancerígeno",
            value: dataDash2.carcinogenic_group_1_count,
            color: "#068190",
            subname: "Grupo 1",
        },
        {
            name: "Grupo 2A - Probablemente cancerígeno",
            value: dataDash2.carcinogenic_group_2A_count,
            color: "#04373D",
            subname: "Grupo 2A",
        },
        {
            name: "Grupo 2B - Posiblemente carcinógeno",
            value: dataDash2.carcinogenic_group_2B_count,
            color: "#117ACB",
            subname: "Grupo 2B",
        },
        {
            name: "Reportado FDS",
            value: dataDash2.carcinogenic_reported_by_sds_count,
            color: "#0FBEF9",
            subname: "Reportado FDS",
        },
    ];

    const totalCarcinogenic = series.reduce((sum, item) => sum + item.value, 0);
    const maxValue = Math.max(...series.map((item) => item.value));
    const yAxisMax = Math.ceil(maxValue * 1.2);

    return {
        title: {
            text: `${totalCarcinogenic} Carcinogénicos`,
            left: "20px",
            top: "10px",
            textStyle: {
                fontFamily: "Poppins",
                fontSize: "16px",
                fontWeight: 500,
                color: "#333",
                rich: {
                    total: {
                        fontSize: "24px",
                        fontWeight: 600,
                        padding: [0, 8, 0, 0],
                    },
                },
            },
        },
        tooltip: {
            trigger: "item",
            axisPointer: {
                type: "none",
            },
            textStyle: {
                fontFamily: "Poppins",
            },
            formatter: function(params) {
                const seriesItem = series.find(item => item.name === params.seriesName);
                return `<div style="padding: 4px;">
                    <div style="display: flex; align-items: center;">
                        <span style="display: inline-block; width: 8px; height: 8px; border-radius: 50%; background-color: ${seriesItem.color}; margin-right: 8px;"></span>
                        <span>${seriesItem.subname}</span>
                        <span style="margin-left: 8px;">${params.value}</span>
                    </div>
                </div>`;
            },
            padding: [8, 12],
            extraCssText: 'box-shadow: 0 2px 12px rgba(0,0,0,0.1); border-radius: 4px; z-index: 100000;'
        },
        grid: {
            left: "3%",
            right: "4%",
            bottom: "25%",
            containLabel: true,
        },
        xAxis: {
            type: "category",
            data: [""],
            axisLabel: {
                show: false,
            },
            axisTick: {
                show: false,
            },
            axisLine: {
                show: false,
            },
        },
        yAxis: {
            type: "value",
            max: yAxisMax,
            splitNumber: 6,
            splitLine: {
                lineStyle: {
                    type: "dashed",
                    color: "#E0E0E0",
                },
            },
            axisLabel: {
                fontFamily: "Poppins",
                color: "#666",
                fontSize: 12,
            },
            axisTick: {
                show: false,
            },
            axisLine: {
                show: false,
            },
        },
        legend: {
            data: series.map((item) => item.name),
            bottom: "0",
            left: "center",
            itemWidth: 8,
            itemHeight: 8,
            textStyle: {
                fontFamily: "Poppins",
                color: "#666",
                fontSize: 11,
            },
            itemGap: 5,
            icon: "circle",
            formatter: function (name) {
                const item = series.find((s) => s.name === name);
                return `${name} (${item.value})`;
            },
        },
        series: series.map((item) => ({
            name: item.name,
            type: "bar",
            data: [item.value],
            itemStyle: {
                color: item.color,
                borderRadius: [15, 15, 0, 0],
            },
            barWidth: "9%",
            barGap: 1.5,
            label: {
                show: true,
                position: "top",
                distance: 5,
                fontFamily: "Poppins",
                fontSize: 12,
                color: "#333",
            },
        })),
    };
}

/**
 * Función para obtener las opciones de configuración de un gráfico de Componentes de priorización normativa.
 *
 * @param {object} dataDash2
 * return {object} options
 */
export function getReprotoxicityChartOptions(dataDash2) {
    if (
        !dataDash2 ||
        typeof dataDash2.product_reprotoxic_with_affected_count ===
            "undefined" ||
        typeof dataDash2.product_reprotoxic_not_affected_count === "undefined"
    ) {
        return {};
    }

    return {
        tooltip: {
            trigger: "item",
            textStyle: {
                fontFamily: "Poppins",
            },
        },
        legend: {
            orient: "vertical",
            left: "65%",
            top: "middle",
            type: "scroll",
            width: "35%",
            textStyle: {
                fontSize: 11,
                overflow: "break",
                fontFamily: "Poppins",
            },
            icon: "circle",
        },
        series: [
            {
                name: "Productos reprotóxicos",
                type: "pie",
                radius: ["40%", "70%"],
                center: ["35%", "50%"],
                avoidLabelOverlap: false,
                itemStyle: {
                    borderRadius: 6,
                    borderColor: "#fff",
                    borderWidth: 2,
                },
                label: {
                    show: true,
                    position: "inside",
                    formatter: "{c}",
                    fontSize: 14,
                    fontWeight: "bold",
                    color: "#fff",
                    textShadowColor: "rgba(0, 0, 0, 0.5)",
                    textShadowBlur: 3,
                    textShadowOffsetX: 1,
                    textShadowOffsetY: 1,
                    fontFamily: "Poppins",
                },
                labelLine: {
                    show: false,
                },
                emphasis: {
                    label: {
                        show: true,
                        fontSize: 16,
                        fontWeight: "bold",
                        fontFamily: "Poppins",
                    },
                    itemStyle: {
                        shadowBlur: 10,
                        shadowOffsetX: 0,
                        shadowColor: "rgba(0, 0, 0, 0.5)",
                    },
                },
                data: [
                    {
                        value: dataDash2.product_reprotoxic_with_affected_count,
                        name: "Con afectación",
                        itemStyle: {
                            color: "#117ACB",
                        },
                    },
                    {
                        value: dataDash2.product_reprotoxic_not_affected_count,
                        name: "Sin afectación",
                        itemStyle: {
                            color: "#0FBEF9",
                        },
                    },
                ],
            },
        ],
    };
}

/**
 * Función para obtener las opciones de configuración de un gráfico de Ototóxicos.
 *
 * @param {object} dataDash2
 * return {object} options
 */
export function getOtoxicityChartOptions(dataDash2) {
    if (
        !dataDash2 ||
        typeof dataDash2.product_ototoxic_not_affected_count === "undefined" ||
        typeof dataDash2.product_ototoxic_with_affected_count === "undefined"
    ) {
        return {};
    }

    return {
        tooltip: {
            trigger: "item",
            textStyle: {
                fontFamily: "Poppins",
            },
        },
        legend: {
            orient: "vertical",
            left: "65%",
            top: "middle",
            type: "scroll",
            width: "35%",
            textStyle: {
                fontSize: 11,
                overflow: "break",
                fontFamily: "Poppins",
            },
            icon: "circle",
        },
        series: [
            {
                name: "Productos ototóxicos",
                type: "pie",
                radius: ["40%", "70%"],
                center: ["35%", "50%"],
                avoidLabelOverlap: false,
                itemStyle: {
                    borderRadius: 6,
                    borderColor: "#fff",
                    borderWidth: 2,
                },
                label: {
                    show: true,
                    position: "inside",
                    formatter: "{c}",
                    fontSize: 14,
                    fontWeight: "bold",
                    color: "#fff",
                    textShadowColor: "rgba(0, 0, 0, 0.5)",
                    textShadowBlur: 3,
                    textShadowOffsetX: 1,
                    textShadowOffsetY: 1,
                    fontFamily: "Poppins",
                },
                labelLine: {
                    show: false,
                },
                emphasis: {
                    label: {
                        show: true,
                        fontSize: 16,
                        fontWeight: "bold",
                        fontFamily: "Poppins",
                    },
                    itemStyle: {
                        shadowBlur: 10,
                        shadowOffsetX: 0,
                        shadowColor: "rgba(0, 0, 0, 0.5)",
                    },
                },
                data: [
                    {
                        value: dataDash2.product_ototoxic_with_affected_count,
                        name: "Con afectación",
                        itemStyle: {
                            color: "#117ACB",
                        },
                    },
                    {
                        value: dataDash2.product_ototoxic_not_affected_count,
                        name: "Sin afectación",
                        itemStyle: {
                            color: "#0FBEF9",
                        },
                    },
                ],
            },
        ],
    };
}

/**
 * Función para obtener las opciones de configuración de un gráfico de Mutagénicos.
 *
 * @param {object} dataDash2
 * return {object} options
 */
export function getMutagenicityChartOptions(dataDash2) {
    if (
        !dataDash2 ||
        typeof dataDash2.product_mutagenic_with_affected_count ===
            "undefined" ||
        typeof dataDash2.product_mutagenic_not_affected_count === "undefined"
    ) {
        return {};
    }

    return {
        tooltip: {
            trigger: "item",
            textStyle: {
                fontFamily: "Poppins",
            },
        },
        legend: {
            orient: "vertical",
            left: "65%",
            top: "middle",
            type: "scroll",
            width: "35%",
            textStyle: {
                fontSize: 11,
                overflow: "break",
                fontFamily: "Poppins",
            },
            icon: "circle",
        },
        series: [
            {
                name: "Productos mutagénicos",
                type: "pie",
                radius: ["40%", "70%"],
                center: ["35%", "50%"],
                avoidLabelOverlap: false,
                itemStyle: {
                    borderRadius: 6,
                    borderColor: "#fff",
                    borderWidth: 2,
                },
                label: {
                    show: true,
                    position: "inside",
                    formatter: "{c}",
                    fontSize: 14,
                    fontWeight: "bold",
                    color: "#fff",
                    textShadowColor: "rgba(0, 0, 0, 0.5)",
                    textShadowBlur: 3,
                    textShadowOffsetX: 1,
                    textShadowOffsetY: 1,
                    fontFamily: "Poppins",
                },
                labelLine: {
                    show: false,
                },
                emphasis: {
                    label: {
                        show: true,
                        fontSize: 16,
                        fontWeight: "bold",
                        fontFamily: "Poppins",
                    },
                    itemStyle: {
                        shadowBlur: 10,
                        shadowOffsetX: 0,
                        shadowColor: "rgba(0, 0, 0, 0.5)",
                    },
                },
                data: [
                    {
                        value: dataDash2.product_mutagenic_with_affected_count,
                        name: "Con afectación",
                        itemStyle: {
                            color: "#117ACB",
                        },
                    },
                    {
                        value: dataDash2.product_mutagenic_not_affected_count,
                        name: "Sin afectación",
                        itemStyle: {
                            color: "#0FBEF9",
                        },
                    },
                ],
            },
        ],
    };
}

/**
 * Función para obtener las opciones de configuración de un gráfico de Componentes de priorización normativa.
 *
 * @param {object} data
 * @param {number} page
 * @param {number} itemsPerPage
 * return {object} options
 */
export const getPriorityChartOptions = (data, page = 1, itemsPerPage = 10) => {
    if (!data || !data.components_priority_normative) return {};

    // Ordenar por valor de mayor a menor
    const sortedData = [...data.components_priority_normative].sort(
        (a, b) => b.value - a.value
    );

    // Calcular índices para la paginación
    const startIndex = (page - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;

    // Obtener solo los datos de la página actual
    const paginatedData = sortedData.slice(startIndex, endIndex);

    // Generar colores únicos para cada segmento
    const usedColors = [];
    const colors = paginatedData.map(() => {
        const color = getRandomColor(usedColors);
        usedColors.push(color);
        return color;
    });

    return {
        tooltip: {
            trigger: "item",
            formatter: (params) => {
                return `${params.name.substring(0, 15)}${
                    params.name.length > 15 ? "..." : ""
                }<br/>Cantidad: ${params.value}`;
            },
        },
        legend: {
            orient: "vertical",
            left: "65%",
            top: "middle",
            type: "scroll",
            width: "35%",
            formatter: (name) => {
                const item = paginatedData.find((d) => d.name === name);
                return `${name.substring(0, 15)}${
                    name.length > 15 ? "..." : ""
                } (${item.value})`;
            },
            textStyle: {
                fontSize: 11,
                overflow: "break",
                fontFamily: "Poppins",
            },
            icon: "circle",
        },
        series: [
            {
                name: "Componentes",
                type: "pie",
                radius: ["40%", "70%"],
                center: ["35%", "50%"],
                avoidLabelOverlap: false,
                itemStyle: {
                    borderRadius: 6,
                    borderColor: "#fff",
                    borderWidth: 2,
                },
                label: {
                    show: true,
                    position: "inside",
                    formatter: "{c}",
                    fontSize: 14,
                    fontWeight: "bold",
                    color: "#fff",
                    textShadowColor: "rgba(0, 0, 0, 0.5)",
                    textShadowBlur: 3,
                    textShadowOffsetX: 1,
                    textShadowOffsetY: 1,
                },
                labelLine: {
                    show: false,
                },
                emphasis: {
                    label: {
                        show: true,
                        fontSize: 16,
                        fontWeight: "bold",
                    },
                    itemStyle: {
                        shadowBlur: 10,
                        shadowOffsetX: 0,
                        shadowColor: "rgba(0, 0, 0, 0.5)",
                    },
                },
                data: paginatedData.map((item, index) => ({
                    value: item.value,
                    name: item.name,
                    itemStyle: {
                        color: colors[index],
                    },
                })),
            },
        ],
    };
};

/**
 * Función para obtener las opciones de configuración de un gráfico de Productos asociados por ubicación.
 *
 * @param {object} dataDash2
 * return {object} options
 */
export function getHealthCategoriesChartOptions(dataDash2) {
    if (
        !dataDash2 ||
        typeof dataDash2.categories_health === "undefined" ||
        dataDash2.categories_health.length === 0
    ) {
        return {};
    }

    // Paleta de colores específica
    const specificColors = [
        "#00968f", // Verde azulado
        "#1b3240", // Azul oscuro
        "#ff6b35", // Naranja
        "#dc3545", // Rojo
        "#17a2b8", // Azul claro
        "#6f42c1", // Morado
        "#20c997", // Verde menta
        "#fd7e14", // Naranja claro
        "#6610f2", // Violeta
        "#28a745", // Verde
    ];

    // Generar los datos para una sola serie treemap
    const treemapData = dataDash2.categories_health.map((item, index) => ({
        name: item.category,
        value: item.productsCount,
        locations: item.locations,
        itemStyle: {
            color: specificColors[index % specificColors.length],
        },
    }));

    return {
        backgroundColor: "#ffffff",
        tooltip: {
            formatter: (params) => {
                const { data } = params;
                const locationsList =
                    data.locations && data.locations.length > 0
                        ? data.locations.join("<br/>")
                        : "Sin ubicaciones";

                return `
                        <div style="padding: 8px; font-family: Poppins;">
                            <b>${data.name}</b><br/>
                            <b>Productos:</b> ${data.value}<br/>
                            <b>Ubicaciones:</b><br/>${locationsList}
                        </div>
                    `;
            },
            textStyle: {
                fontFamily: "Poppins",
            },
            backgroundColor: "rgba(255, 255, 255, 0.95)",
            borderColor: "#eee",
            borderWidth: 1,
            extraCssText: "box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);",
        },

        series: [
            {
                name: "Categorías",
                type: "treemap",
                width: "95%",
                height: "95%",
                left: "center",
                top: "center",
                data: treemapData,
                roam: false,
                nodeClick: false,
                breadcrumb: { show: false },
                label: {
                    show: false,
                    position: "inside",
                    formatter: function (params) {
                        // Truncar el texto si es muy largo
                        const name = params.name;
                        const value = params.value;
                        const maxLength = 20;

                        const truncatedName =
                            name.length > maxLength
                                ? name.substring(0, maxLength) + "..."
                                : name;

                        return `${truncatedName}\n${value}`;
                    },
                    fontSize: function (params) {
                        // Ajustar el tamaño de fuente según el área del bloque
                        const area =
                            params.treeAncestors[
                                params.treeAncestors.length - 1
                            ].area;
                        if (area < 2000) return 10;
                        if (area < 5000) return 12;
                        if (area < 10000) return 14;
                        return 16;
                    },
                    fontFamily: "Poppins, sans-serif",
                    color: "#ffffff",
                    fontWeight: 500,
                    width: "90%",
                    overflow: "truncate",
                    lineHeight: 16,
                    align: "center",
                    verticalAlign: "middle",
                },
                itemStyle: {
                    borderColor: "#fff",
                    borderWidth: 2,
                    gapWidth: 2,
                    borderRadius: 4,
                },
                emphasis: {
                    itemStyle: {
                        shadowBlur: 10,
                        shadowColor: "rgba(0,0,0,0.3)",
                    },
                    label: {
                        fontWeight: "bold",
                        fontSize: function (params) {
                            // Incrementar ligeramente el tamaño en hover
                            const area =
                                params.treeAncestors[
                                    params.treeAncestors.length - 1
                                ].area;
                            if (area < 2000) return 11;
                            if (area < 5000) return 13;
                            if (area < 10000) return 15;
                            return 17;
                        },
                    },
                },
                levels: [
                    {
                        itemStyle: {
                            borderWidth: 2,
                            gapWidth: 2,
                            borderColor: "#fff",
                        },
                    },
                ],
            },
        ],
    };
}

/**
 * Función para obtener las opciones de configuración de un gráfico de Mutagénicos.
 *
 * @param {object} dataDash2
 * return {object} options
 */
export function getNeurotoxicChartOptions(dataDash2) {
    // Validate input data
    if (
        !dataDash2 ||
        typeof dataDash2.levels_neurotoxic === "undefined" ||
        typeof dataDash2.product_neurotoxic_count === "undefined"
    ) {
        return {};
    }

    const categories = ["Nivel 1", "Nivel 2", "Nivel 3", "Nivel 4", "Otros"];
    const data = [
        dataDash2.levels_neurotoxic.level_1 || 0,
        dataDash2.levels_neurotoxic.level_2 || 0,
        dataDash2.levels_neurotoxic.level_3 || 0,
        dataDash2.levels_neurotoxic.level_4 || 0,
        dataDash2.levels_neurotoxic.level_for_sga || 0,
    ];

    return {
        title: {
            text: `${dataDash2.product_neurotoxic_count} Neurotóxicos`,
            left: "20px",
            top: "10px",
            textStyle: {
                fontFamily: "Poppins",
                fontSize: "16px",
                fontWeight: 500,
                color: "#333",
                rich: {
                    total: {
                        fontSize: "24px",
                        fontWeight: 600,
                        padding: [0, 8, 0, 0],
                    },
                },
            },
        },
        tooltip: {
            trigger: "axis",
            axisPointer: {
                type: "shadow",
            },
            formatter: "{b}: {c}",
        },
        legend: {
            data: ["Niveles de neurotoxicidad"],
            textStyle: {
                fontFamily: "Poppins",
                color: "#333",
            },
            top: "5%",
            icon: "circle",
        },
        grid: {
            top: "15%",
            left: "5%",
            right: "5%",
            bottom: "10%",
            containLabel: true,
        },
        xAxis: {
            type: "category",
            data: categories,
            axisLabel: {
                fontFamily: "Poppins",
                color: "#666",
                fontSize: 12,
            },
            axisTick: {
                show: false,
            },
            axisLine: {
                show: true,
                lineStyle: {
                    color: "#E0E0E0",
                },
            },
        },
        yAxis: {
            type: "value",
            splitLine: {
                lineStyle: {
                    type: "dashed",
                    color: "#E0E0E0",
                },
            },
            axisLabel: {
                fontFamily: "Poppins",
                color: "#666",
                fontSize: 12,
            },
            axisTick: {
                show: false,
            },
            axisLine: {
                show: false,
            },
        },
        series: [
            {
                type: "bar",
                data: data.map((value, index) => ({
                    value: value,
                    itemStyle: {
                        color: getBarColor(index),
                        borderRadius: [15, 15, 0, 0],
                    },
                })),
                barWidth: "50%",
                emphasis: {
                    itemStyle: {
                        brightness: 0.1,
                    },
                },
            },
        ],
    };
}

/**
 * Función para obtener el color de una barra.
 *
 * @param {number} index
 * @return {string} color
 */
function getBarColor(index) {
    const colors = [
        "#068190", // Nivel 1 - Teal
        "#04373D", // Nivel 2 - Dark Blue
        "#674485", // Nivel 3 - Orange
        "#117ACB", // Nivel 4 - Red
        "#B45DFF", // SGA - Purple
    ];
    return colors[index];
}

/**
 * Función para obtener las opciones de configuración de un gráfico de PQ Efectos Corrosivos.
 *
 * @param {object} dataDash2
 * return {object} options
 */
export function getCorrosiveChartOptions(dataDash2) {
    if (
        !dataDash2.product_corrosive_ocular_count === "undefined" ||
        !dataDash2.product_corrosive_dermal_count === "undefined"
    ) {
        return {};
    }

    return {
        title: {
            text: `${
                dataDash2.product_corrosive_total || 0
            } Productos corrosivos`,
            left: "20px",
            top: "10px",
            textStyle: {
                fontFamily: "Poppins",
                fontSize: "16px",
                fontWeight: 500,
                color: "#333",
            },
        },
        tooltip: {
            trigger: "axis",
            axisPointer: {
                type: "shadow",
            },
            textStyle: {
                fontFamily: "Poppins",
            },
        },
        legend: {
            show: true,
            bottom: "0%",
            left: "center",
            itemWidth: 10,
            itemHeight: 10,
            textStyle: {
                fontFamily: "Poppins",
                color: "#666",
                fontSize: 14,
            },
            itemGap: 30,
            icon: "circle",
        },
        grid: {
            top: "25%",
            left: "3%",
            right: "4%",
            bottom: "15%",
            containLabel: true,
        },
        xAxis: {
            type: "value",
            splitLine: {
                lineStyle: {
                    type: "dashed",
                    color: "#E0E0E0",
                },
            },
            axisLabel: {
                fontFamily: "Poppins",
                color: "#666",
                fontSize: 12,
            },
            axisTick: {
                show: false,
            },
            axisLine: {
                show: false,
            },
        },
        yAxis: {
            type: "category",
            data: ["", ""],
            axisLabel: {
                show: false,
            },
            axisTick: {
                show: false,
            },
            axisLine: {
                show: true,
                lineStyle: {
                    color: "#E0E0E0",
                },
            },
        },
        series: [
            {
                name: "Ojos",
                type: "bar",
                stack: "total",
                data: [dataDash2.product_corrosive_ocular_count || 0, 0],
                itemStyle: {
                    color: "#04373D",
                    borderRadius: [0, 15, 15, 0],
                },
                barWidth: "40%",
            },
            {
                name: "Piel",
                type: "bar",
                stack: "total",
                data: [0, dataDash2.product_corrosive_dermal_count || 0],
                itemStyle: {
                    color: "#0FBEF9",
                    borderRadius: [0, 15, 15, 0],
                },
                barWidth: "40%",
            },
        ],
    };
}

/**
 * Función para obtener las opciones de configuración de un gráfico de Sensibilizantes.
 *
 * @param {object} dataDash2
 * return {object} options
 */
export function getSensitizingChartOptions(dataDash2) {
    // Validate input data
    if (
        !dataDash2 ||
        typeof dataDash2.sga_sensitizing_dermal_count === "undefined" ||
        typeof dataDash2.sga_sensitizing_respiratory_count === "undefined"
    ) {
        return {};
    }

    const categories = ["Cutánea", "Respiratorios"];
    const data = [
        dataDash2.sga_sensitizing_dermal_count || 0,
        dataDash2.sga_sensitizing_respiratory_count || 0,
    ];

    return {
       /*  title: {
            text: `${
                dataDash2.sga_sensitizing_dermal_count +
                dataDash2.sga_sensitizing_respiratory_count
            } Sensibilizantes`,
            left: "20px",
            top: "10px",
            textStyle: {
                fontFamily: "Poppins",
                fontSize: "16px",
                fontWeight: 500,
                color: "#333",
                rich: {
                    total: {
                        fontSize: "24px",
                        fontWeight: 600,
                        padding: [0, 8, 0, 0],
                    },
                },
            },
        }, */
        tooltip: {
            trigger: "axis",
            axisPointer: {
                type: "shadow",
            },
            formatter: "{b}: {c}",
        },
        legend: {
            data: ["Sensibilizantes"],
            textStyle: {
                fontFamily: "Poppins",
                color: "#333",
            },
            top: "5%",
            icon: "circle",
        },
        grid: {
            top: "12%",
            left: "5%",
            right: "5%",
            bottom: "10%",
            containLabel: true,
        },
        xAxis: {
            type: "category",
            data: categories,
            axisLabel: {
                fontFamily: "Poppins",
                color: "#666",
                fontSize: 12,
            },
            axisTick: {
                show: false,
            },
            axisLine: {
                show: true,
                lineStyle: {
                    color: "#E0E0E0",
                },
            },
        },
        yAxis: {
            type: "value",
            splitLine: {
                lineStyle: {
                    type: "dashed",
                    color: "#E0E0E0",
                },
            },
            axisLabel: {
                fontFamily: "Poppins",
                color: "#666",
                fontSize: 12,
            },
            axisTick: {
                show: false,
            },
            axisLine: {
                show: false,
            },
        },
        series: [
            {
                type: "bar",
                data: data.map((value, index) => ({
                    value: value,
                    itemStyle: {
                        color: getBarColor(index),
                        borderRadius: [15, 15, 0, 0],
                    },
                })),
                barWidth: "20%",
                emphasis: {
                    itemStyle: {
                        brightness: 0.1,
                    },
                },
            },
        ],
    };
}

/**
 * Función para obtener las opciones de configuración de un gráfico de estado de inventario.
 *
 * @param {object} dataDash1
 * return {object} options
 */
export function getStatusInventoryPQChartOptions(dataDash1) {
    if (
        !dataDash1 ||
        typeof dataDash1.total_inventory_verified === "undefined" ||
        typeof dataDash1.total_inventory_for_verification_cumply ===
            "undefined" ||
        typeof dataDash1.total_inventory_for_verification_not_cumply ===
            "undefined" ||
        typeof dataDash1.not_document === "undefined"
    ) {
        return {};
    }

    const series = [
        {
            name: "Caracterizados",
            value: dataDash1.total_inventory_verified,
            color: "#35C895",
            key: "caracterized",
            title: "Caracterizados",
        },
        {
            name: "Sin caracterizar cumple",
            value: dataDash1.total_inventory_for_verification_cumply,
            color: "#0FBEF9",
            key: "not_caracterized_with_cumply",
            title: "Sin caracterizar cumple",
        },
        {
            name: "Sin caracterizar no cumple",
            value: dataDash1.total_inventory_for_verification_not_cumply,
            color: "#F27833",
            key: "not_caracterized_not_cumply",
            title: "Sin caracterizar no cumple",
        },
        {
            name: "Sin documento",
            value: dataDash1.not_document,
            color: "#E84432",
            key: "not_document",
            title: "Sin documento",
        },
    ];

    const maxValue = Math.max(...series.map((item) => item.value));
    const yAxisMax = Math.ceil(maxValue * 1.2);

    return {
        tooltip: {
            trigger: "item",
            axisPointer: {
                type: "none",
            },
            textStyle: {
                fontFamily: "Poppins",
            },
        },
        grid: {
            left: "3%",
            right: "4%",
            bottom: "25%",
            top: "13%",
            containLabel: true,
        },
        xAxis: {
            type: "category",
            data: [""],
            axisLabel: {
                show: false,
            },
            axisTick: {
                show: false,
            },
            axisLine: {
                show: false,
            },
        },
        yAxis: {
            type: "value",
            max: yAxisMax,
            splitNumber: 6,
            splitLine: {
                lineStyle: {
                    type: "dashed",
                    color: "#E0E0E0",
                },
            },
            axisLabel: {
                fontFamily: "Poppins",
                color: "#666",
                fontSize: 12,
            },
            axisTick: {
                show: false,
            },
            axisLine: {
                show: false,
            },
        },
        legend: {
            data: series.map((item) => item.name),
            bottom: "0",
            left: "center",
            itemWidth: 8,
            itemHeight: 8,
            textStyle: {
                fontFamily: "Poppins",
                color: "#666",
                fontSize: 11,
            },
            itemGap: 5,
            icon: "circle",
            formatter: function (name) {
                const item = series.find((s) => s.name === name);
                return `${name} (${item.value})`;
            },
        },
        series: series.map((item) => ({
            name: item.name,
            type: "bar",
            data: [item],
            itemStyle: {
                color: item.color,
                borderRadius: [15, 15, 0, 0],
            },
            barWidth: "9%",
            barGap: 1.5,
            label: {
                show: true,
                position: "top",
                distance: 5,
                fontFamily: "Poppins",
                fontSize: 12,
                color: "#333",
            },
            emphasis: {
                itemStyle: {
                    shadowBlur: 10,
                    shadowOffsetX: 0,
                    shadowColor: "rgba(0, 0, 0, 0.5)",
                    opacity: 0.8,
                },
            },
        })),
        onclick: function (params) {
            if (params.seriesType === "bar") {
                const item = series.find((d) => d.name === params.name);
                if (item && item.key) {
                    const event = new CustomEvent("chart-segment-clicked", {
                        detail: {
                            key: item.key,
                            value: true,
                            title: item.title,
                        },
                    });
                    document.dispatchEvent(event);
                }
            }
        },
    };
}

/**
 * Función para obtener las opciones de configuración de un gráfico de  peligrosidad del inventario.
 *
 * @param {object} dataDash1
 * return {object} options
 */
export function getRiskInventoryChartOptions(dataDash1) {
    if (
        !dataDash1 ||
        typeof dataDash1.sga_physical_total === "undefined" ||
        typeof dataDash1.sga_health_total === "undefined" ||
        typeof dataDash1.sga_environment_total === "undefined"
    ) {
        return {};
    }

    const data = [
        {
            value: dataDash1.sga_physical_total,
            name: "Físico",
            itemStyle: {
                color: "#0FBEF9",
            },
            key: "sga_physical",
            title: "SGA Físico",
        },
        {
            value: dataDash1.sga_health_total,
            name: "Salud",
            itemStyle: {
                color: "#1C7FCB",
            },
            key: "sga_health",
            title: "SGA Salud",
        },
        {
            value: dataDash1.sga_environment_total,
            name: "Ambiental",
            itemStyle: {
                color: "#35C895",
            },
            key: "sga_environment",
            title: "SGA Ambiental",
        },
    ];

    return {
        tooltip: {
            trigger: "item",
            textStyle: {
                fontFamily: "Poppins",
            },
        },
        legend: {
            orient: 'vertical',
            right: '5%',
            top: 'middle',
            itemWidth: 8,
            itemHeight: 8,
            textStyle: {
                fontFamily: "Poppins",
                color: "#666",
                fontSize: 11,
            },
            itemGap: 15,
            icon: "circle",
            formatter: function (name) {
                const item = data.find((s) => s.name === name);
                return `${name} (${item.value})`;
            },
        },
        grid: {
            containLabel: true
        },
        series: [
            {
                type: "pie",
                radius: ['45%', '80%'],
                center: ['42%', '50%'],
                avoidLabelOverlap: false,
                itemStyle: {
                    borderRadius: 6,
                    borderColor: "#fff",
                    borderWidth: 2,
                },
                label: {
                    show: false,
                    position: "inside",
                    formatter: "{c}",
                    borderWidth: 1,
                    borderRadius: 4,
                    fontSize: 14,
                    fontWeight: "bold",
                    color: "#fff",
                    textShadowColor: "rgba(0, 0, 0, 0.5)",
                    textShadowBlur: 3,
                    textShadowOffsetX: 1,
                    textShadowOffsetY: 1,
                    fontFamily: "Poppins",
                },
                labelLine: {
                    show: false,
                },
                emphasis: {
                    label: {
                        show: true,
                        fontSize: 16,
                        fontWeight: "bold",
                        fontFamily: "Poppins",
                    },
                    itemStyle: {
                        shadowBlur: 10,
                        shadowOffsetX: 0,
                        shadowColor: "rgba(0, 0, 0, 0.5)",
                    },
                },
                data: data,
            },
        ],
        media: [
            {
                query: {
                    maxWidth: 480
                },
                option: {
                    legend: {
                        right: '3%',
                        itemGap: 10,
                        textStyle: {
                            fontSize: 10
                        }
                    },
                    series: [
                        {
                            center: ['35%', '50%'],
                            radius: ['40%', '70%']
                        }
                    ]
                }
            }
        ],
        onclick: function (params) {
            if (params.seriesType === "pie") {
                const item = data.find((d) => d.name === params.name);
                if (item && item.key) {
                    const event = new CustomEvent("chart-segment-clicked", {
                        detail: {
                            key: item.key,
                            value: true,
                            title: item.title,
                        },
                    });
                    document.dispatchEvent(event);
                }
            }
        },
    };
}

/**
 * Función para obtener las opciones de configuración de un gráfico de  uso y desuso.
 *
 * @param {object} dataDash1
 * return {object} options
 */
export function getUseandDesuseChartOptions(dataDash1) {
    if (
        !dataDash1 ||
        typeof dataDash1.used === "undefined" ||
        typeof dataDash1.not_use === "undefined"
    ) {
        return {};
    }

    const data = [
        {
            value: dataDash1.used,
            name: "Si",
            itemStyle: {
                color: "#0FBEF9",
            },
            key: "used",
            title: "Disponible en el inventario",
        },
        {
            value: dataDash1.not_use,
            name: "No",
            itemStyle: {
                color: "#1C7FCB",
            },
            key: "not_use",
            title: "No disponible en el inventario",
        },
    ];

    return {
        tooltip: {
            trigger: "item",
            textStyle: {
                fontFamily: "Poppins",
            },
        },
        legend: {
            orient: 'vertical',
            right: '12%',
            top: 'middle',
            itemWidth: 8,
            itemHeight: 8,
            textStyle: {
                fontFamily: "Poppins",
                color: "#666",
                fontSize: 11,
            },
            itemGap: 15,
            icon: "circle",
            formatter: function (name) {
                const item = data.find((s) => s.name === name);
                return `${name} (${item.value})`;
            },
        },
        grid: {
            containLabel: true
        },
        series: [
            {
                type: "pie",
                radius: ['45%', '80%'],
                center: ['42%', '50%'],
                avoidLabelOverlap: false,
                itemStyle: {
                    borderRadius: 6,
                    borderColor: "#fff",
                    borderWidth: 2,
                },
                label: {
                    show: false,
                    position: "inside",
                    formatter: "{c}",
                    borderWidth: 1,
                    borderRadius: 4,
                    fontSize: 14,
                    fontWeight: "bold",
                    color: "#fff",
                    textShadowColor: "rgba(0, 0, 0, 0.5)",
                    textShadowBlur: 3,
                    textShadowOffsetX: 1,
                    textShadowOffsetY: 1,
                    fontFamily: "Poppins",
                },
                labelLine: {
                    show: false,
                },
                emphasis: {
                    label: {
                        show: true,
                        fontSize: 16,
                        fontWeight: "bold",
                        fontFamily: "Poppins",
                    },
                    itemStyle: {
                        shadowBlur: 10,
                        shadowOffsetX: 0,
                        shadowColor: "rgba(0, 0, 0, 0.5)",
                    },
                },
                data: data,
            },
        ],
        media: [
            {
                query: {
                    maxWidth: 480
                },
                option: {
                    legend: {
                        right: '14%',
                        itemGap: 10,
                        textStyle: {
                            fontSize: 10
                        }
                    },
                    series: [
                        {
                            center: ['35%', '50%'],
                            radius: ['40%', '70%']
                        }
                    ]
                }
            }
        ],
        onclick: function (params) {
            if (params.seriesType === "pie") {
                const item = data.find((d) => d.name === params.name);
                if (item && item.key) {
                    const event = new CustomEvent("chart-segment-clicked", {
                        detail: {
                            key: item.key,
                            value: true,
                            title: item.title,
                        },
                    });
                    document.dispatchEvent(event);
                }
            }
        },
    };
}

/**
 * Función para obtener las opciones de configuración de un gráfico de barras para productos sugetos a regulacion.
 *
 * @param {object} dataDash1
 * return {object} options
 */
export function getOptionsBarsSuggestionRegulation(dataDash1) {
    if (
        !dataDash1 ||
        typeof dataDash1.sga_death_total === "undefined" ||
        typeof dataDash1.carcinogenic_total === "undefined" ||
        typeof dataDash1.un_danger_total === "undefined" ||
        typeof dataDash1.ppam_total === "undefined"
    ) {
        return {};
    }

    const data = [
        /* dataDash1.sga_controlled_substances_total, */
        {
            value: dataDash1.sga_death_total,
            key: "sga_death",
            title: "Mortales",
        },
        {
            value: dataDash1.carcinogenic_total,
            key: "carcinogenic",
            title: "Cancerígenos",
        },
        {
            value: dataDash1.un_danger_total,
            key: "un_danger",
            title: "Mercancías peligrosas",
        },
        { value: dataDash1.ppam_total, key: "ppam", title: "PPAM" },
    ];

    return {
        tooltip: {
            trigger: "axis",
            axisPointer: {
                type: "shadow",
            },
        },
        grid: {
            left: "3%",
            right: "3%",
            bottom: "3%",
            top: "5%",
            containLabel: true,
        },
        xAxis: {
            type: "value",
            boundaryGap: [0, 0.01],
        },
        yAxis: {
            type: "category",
            data: [
                /*  "Sust Controladas", */
                "Mortales",
                "Cancerígenos",
                "Mercancías peligrosas",
                "PPAM",
            ],
            axisLabel: {
                fontSize: 10,
            },
        },
        series: [
            {
                name: "Cantidad",
                type: "bar",
                data: data,
                itemStyle: {
                    color: function (params) {
                        const colors = [
                            "#0FBEF9",
                            "#E84432",
                            "#F27833",
                            "#35C895",
                        ];
                        return colors[params.dataIndex];
                    },

                    borderColor: "#fff",
                    borderWidth: 1,
                    borderRadius: [0, 15, 15, 0],
                },
                label: {
                    show: true,
                    position: "",
                },
            },
        ],

        onclick: function (params) {
            if (params.seriesType === "bar") {
                const item = data.find((d) => d.name === params.name);
                if (item && item.key) {
                    // Log parameters for testing

                    // Dispatch custom event that can be captured by Vue component
                    const event = new CustomEvent("chart-segment-clicked", {
                        detail: {
                            key: item.key,
                            value: true,
                            title: item.title,
                        },
                    });
                    document.dispatchEvent(event);
                }
            }
        },
    };
}

/**
 * Función para obtener las opciones de configuración de un gráfico de beneficios por años.
 *
 * @param {object} dataDash1
 * @return {object} options
 */
export function getBenefitForYearsChartOptions(dataDash1) {
    if (
        !dataDash1 ||
        !dataDash1.benefirs_for_years ||
        !dataDash1.total_benefits
    ) {
        return {};
    }

    // Extract years and values from the data
    const months = Object.keys(dataDash1.benefirs_for_years);
    const values = Object.values(dataDash1.benefirs_for_years);
    const totalBenefits = dataDash1.total_benefits;

    // Función para convertir el mes y año a un objeto Date
    const parseDate = (monthYear) => {
        const [month, year] = monthYear.split(' ');
        const monthIndex = {
            'Enero': 0, 'Febrero': 1, 'Marzo': 2, 'Abril': 3,
            'Mayo': 4, 'Junio': 5, 'Julio': 6, 'Agosto': 7,
            'Septiembre': 8, 'Octubre': 9, 'Noviembre': 10, 'Diciembre': 11
        };
        return new Date(year, monthIndex[month]);
    };

    // Crear array de pares [mes, valor] y ordenarlos
    const sortedData = months.map((month, index) => ({
        month,
        value: values[index]
    })).sort((a, b) => parseDate(a.month) - parseDate(b.month));

    // Extraer los meses y valores ordenados
    const sortedMonths = sortedData.map(item => item.month);
    const sortedValues = sortedData.map(item => item.value);

    return {
        color: ["#36a2eb"],
        grid: {
            left: "3%",
            right: "4%",
            bottom: "3%",
            top: "15%",
            containLabel: true,
        },
        xAxis: {
            type: "category",
            data: sortedMonths,
            axisLine: {
                lineStyle: {
                    color: "#ddd",
                },
            },
            axisLabel: {
                color: "#666",
            },
        },
        yAxis: {
            type: "value",
            splitLine: {
                lineStyle: {
                    type: "dashed",
                    color: "#ddd",
                },
            },
            axisLabel: {
                color: "#666",
            },
        },
        legend: {
            top: "0%",
            left: "center",
            itemWidth: 8,
            itemHeight: 8,
            textStyle: {
                fontFamily: "Poppins",
                color: "#666",
                fontSize: 11,
            },
            itemGap: 5,
            icon: "circle",
            formatter: () => `Beneficiados (${totalBenefits})`,
        },
        toolbox: {
            show: true,
            itemSize: 15,
            itemGap: 10,
            right: "10%",
            feature: {
                magicType: {
                    type: ["line", "bar"],
                    title: {
                        line: "Gráfico de línea",
                        bar: "Gráfico de barras",
                    },
                },
                restore: {
                    title: "Restaurar",
                },
                saveAsImage: {
                    pixelRatio: 2,
                    title: "Guardar como imagen",
                },
            },
        },
        series: [
            {
                name: "Beneficiados",
                data: sortedValues,
                type: "line",
                smooth: true,
                symbol: "circle",
                symbolSize: 6,
                lineStyle: {
                    width: 3,
                },
                itemStyle: {
                    borderRadius: [15, 15, 0, 0],
                },
            },
        ],
        tooltip: {
            trigger: "axis",
            backgroundColor: "rgba(255, 255, 255, 0.95)",
            borderColor: "#eee",
            borderWidth: 1,
            padding: [10, 15],
            textStyle: {
                color: "#333",
                fontFamily: "Poppins",
                fontSize: 13,
            },
            formatter: function (params) {
                const data = params[0];
                return `
                    <div style="display: flex; align-items: center; margin-bottom: 5px;">
                        <span style="font-weight: bold; font-size: 14px; color: #333;">${data.name}</span>
                    </div>
                    <div style="display: flex; align-items: center; margin-top: 8px;">
                        <span style="display: inline-block; width: 10px; height: 10px; border-radius: 50%; background-color: ${data.color}; margin-right: 8px;"></span>
                        <span style="color: #666;">Beneficiados:</span>
                        <span style="font-weight: bold; margin-left: 5px; color: #333;">${data.value}</span>
                    </div>
                `;
            },
            extraCssText:
                "box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1); border-radius: 8px;",
        },
    };
}

/**
 * Función para obtener las opciones de configuración de un gráfico de Analisis de riesgos.
 *
 * @param {object} dataDash1
 * @return {object} options
 */
export function getRiskAnalysisChartOptions(dataDash1) {
    if (!dataDash1 || !dataDash1.priority || !dataDash1.priority.total) {
        return {};
    }

    const series = [
        {
            name: "Prioridad Alta",
            value: dataDash1.priority.priority_1,
            color: "#E84432",
        },
        {
            name: "Prioridad Media",
            value: dataDash1.priority.priority_2,
            color: "#F9D70F",
        },
        {
            name: "Prioridad Baja",
            value: dataDash1.priority.priority_3,
            color: "#35C895",
        },
    ];

    const filteredTotal = series.reduce((sum, item) => sum + item.value, 0);
    const totalTasks = dataDash1.priority.total || filteredTotal;

    return {
        color: series.map((item) => item.color),
        grid: {
            left: "5%", // Aumentado para dar más espacio a la izquierda
            right: "10%", // Aumentado para evitar que la gráfica se corte a la derecha
            bottom: "5%", // Ajustado para mejor espaciado
            top: "25%", // Espacio para título y leyenda
            containLabel: true, // Asegura que las etiquetas estén dentro del área visible
        },
        tooltip: {
            trigger: "item",
            backgroundColor: "rgba(255, 255, 255, 0.95)",
            borderColor: "#eee",
            borderWidth: 1,
            padding: [10, 15],
            textStyle: {
                color: "#333",
                fontFamily: "Poppins, sans-serif",
                fontSize: 13,
            },
            formatter: function (params) {
                return `
            <div style="display: flex; align-items: center; margin-bottom: 5px;">
              <span style="font-weight: bold; font-size: 14px; color: #333;">${
                  params.name
              }</span>
            </div>
            <div style="display: flex; align-items: center; margin-top: 8px;">
              <span style="display: inline-block; width: 10px; height: 10px; border-radius: 50%; background-color: ${
                  params.color
              }; margin-right: 8px;"></span>
              <span style="color: #666;">Cantidad:</span>
              <span style="font-weight: bold; margin-left: 5px; color: #333;">${
                  params.value
              }</span>
              <span style="color: #666; margin-left: 5px;">(${Math.round(
                  (params.value / totalTasks) * 100
              )}%)</span>
            </div>
          `;
            },
            extraCssText:
                "box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1); border-radius: 8px;",
        },
        legend: {
            top: "0%",
            left: "center",
            itemWidth: 8,
            itemHeight: 8,
            textStyle: {
                fontFamily: "Poppins, sans-serif",
                color: "#666",
                fontSize: 11,
            },
            itemGap: 15,
            icon: "circle",
            formatter: function (name) {
                const item = series.find((s) => s.name === name);
                if (item) {
                    return `${name} (${item.value})`;
                }
                return name;
            },
            data: series.map((item) => item.name),
        },
        toolbox: {
            show: true,
            itemSize: 15,
            itemGap: 10,
            right: "10%",
            feature: {
                magicType: {
                    type: ["bar", "pie", "line"],
                    title: {
                        bar: "Gráfico de barras",
                        pie: "Gráfico circular",
                        line: "Gráfico de línea",
                    },
                },
                restore: {
                    title: "Restaurar",
                },
                saveAsImage: {
                    pixelRatio: 2,
                    title: "Guardar como imagen",
                },
            },
        },
        title: {
            text: `Tareas analizadas: ${filteredTotal} `,
            textStyle: {
                fontSize: 16,
                fontWeight: "normal",
                color: "#333",
                fontFamily: "Poppins, sans-serif",
            },
            left: "center",
            top: "30px",
        },
        xAxis: {
            type: "category",
            data: series.map((item) => item.name),
            axisLine: {
                lineStyle: {
                    color: "#ddd",
                },
            },
            axisLabel: {
                color: "#666",
                fontFamily: "Poppins, sans-serif",
                fontSize: 12,
                interval: 0,
                rotate: 0,
            },
        },
        yAxis: {
            type: "value",
            splitLine: {
                lineStyle: {
                    type: "dashed",
                    color: "#ddd",
                },
            },
            axisLabel: {
                color: "#666",
                fontFamily: "Poppins, sans-serif",
                fontSize: 12,
            },
        },
        series: [
            {
                name: "Prioridad",
                type: "bar", // Tipo inicial, cambia a "line" con magicType
                barWidth: "30%",
                data: series.map((item) => ({
                    value: item.value,
                    name: item.name,
                    itemStyle: {
                        color: item.color, // Mantiene el color de prioridad en los puntos
                    },
                })),
                emphasis: {
                    itemStyle: {
                        shadowBlur: 10,
                        shadowOffsetX: 0,
                        shadowColor: "rgba(0, 0, 0, 0.5)",
                    },
                },
                itemStyle: {
                    borderRadius: [15, 15, 0, 0], // Específico para barras
                    borderColor: "#fff",
                    borderWidth: 1,
                },
                label: {
                    show: true,
                    position: "top",
                    formatter: "{c}",
                    fontSize: 12,
                    fontFamily: "Poppins, sans-serif",
                    color: "#666",
                },
                // Propiedades para el modo "line"
                smooth: true, // Líneas curvas
                lineStyle: {
                    color: "#36A2EB", // Color azul específico para la línea
                    width: 3,
                },
                symbolSize: 10, // Puntos más grandes en el modo "line"
            },
        ],
    };
}

/**
 * Función para obtener las opciones de configuración de un gráfico de Toxicidad Agude - Toxicos.
 *
 * @param {object} dataDash2
 * return {object} options
 */
export function getAgutetoxicityChartOptions(dataDash2) {
    if (
        !dataDash2 ||
        typeof dataDash2.product_toxicity_agude_ingestion_count === "undefined" ||
        typeof dataDash2.product_toxicity_inhalation_count === "undefined" ||
        typeof dataDash2.product_toxicity_dermal_count === "undefined"
    ) {
        return {};
    }

    const series = [
        {
            name: "Ingestión",
            value: dataDash2.product_toxicity_agude_ingestion_count,
            color: "#04373D",
        },
        {
            name: "Dérmica",
            value: dataDash2.product_toxicity_dermal_count,
            color: "#117ACB",
        },
        {
            name: "Inhalación",
            value: dataDash2.product_toxicity_inhalation_count,
            color: "#0FBEF9",
        },
    ];

    const maxValue = Math.max(...series.map((item) => item.value));
    const yAxisMax = Math.ceil(maxValue * 1.2);

    return {
       /*  title: {
            text: `${totalToxicity} Toxicidad Aguda - Toxicos`,
            left: "20px",
            top: "10px",
            textStyle: {
                fontFamily: "Poppins",
                fontSize: "16px",
                fontWeight: 500,
                color: "#333",
                rich: {
                    total: {
                        fontSize: "24px",
                        fontWeight: 600,
                        padding: [0, 8, 0, 0],
                    },
                },
            },
        }, */
        tooltip: {
            trigger: "axis",
            axisPointer: {
                type: "shadow",
            },
            formatter: "{b}: {c}",
        },
        grid: {
            top: "12%",
            left: "5%",
            right: "5%",
            bottom: "10%",
            containLabel: true,
        },
        xAxis: {
            type: "category",
            data: series.map(item => item.name),
            axisLabel: {
                fontFamily: "Poppins",
                color: "#666",
                fontSize: 12,
            },
            axisTick: {
                show: false,
            },
            axisLine: {
                show: true,
                lineStyle: {
                    color: "#E0E0E0",
                },
            },
        },
        yAxis: {
            type: "value",
            max: yAxisMax,
            splitNumber: 6,
            splitLine: {
                lineStyle: {
                    type: "dashed",
                    color: "#E0E0E0",
                },
            },
            axisLabel: {
                fontFamily: "Poppins",
                color: "#666",
                fontSize: 12,
            },
            axisTick: {
                show: false,
            },
            axisLine: {
                show: false,
            },
        },
        series: [
            {
                type: "bar",
                data: series.map(item => ({
                    value: item.value,
                    itemStyle: {
                        color: item.color,
                        borderRadius: [15, 15, 0, 0],
                    },
                })),
                barWidth: "28%",
                emphasis: {
                    itemStyle: {
                        brightness: 0.1,
                    },
                },
            },
        ],
    };
}

/**
 * Función para obtener las opciones de configuración de un gráfico de Organos de Target.
 *
 * @param {object} dataDash2
 * return {object} options
 */
export function getTargetOrgansChartOptions(dataDash2) {
    if (
        !dataDash2 ||
        typeof dataDash2.product_target_organs_exposition_repetitive_count === "undefined" ||
        typeof dataDash2.product_target_organs_exposition_unique_count === "undefined"
    ) {
        return {};
    }

    const series = [
        {
            name: "Exposición repetida",
            value: dataDash2.product_target_organs_exposition_repetitive_count,
            color: "#117ACB",
        },
        {
            name: "Exposición única",
            value: dataDash2.product_target_organs_exposition_unique_count,
            color: "#0FBEF9",
        },
    ];
    const maxValue = Math.max(...series.map((item) => item.value));
    const yAxisMax = Math.ceil(maxValue * 1.2);

    return {
        /* title: {
            text: `${totalOrgans} Órganos Diana`,
            left: "20px",
            top: "10px",
            textStyle: {
                fontFamily: "Poppins",
                fontSize: "16px",
                fontWeight: 500,
                color: "#333",
                rich: {
                    total: {
                        fontSize: "24px",
                        fontWeight: 600,
                        padding: [0, 8, 0, 0],
                    },
                },
            },
        }, */
        tooltip: {
            trigger: "axis",
            axisPointer: {
                type: "shadow",
            },
            formatter: "{b}: {c}",
        },
        grid: {
            top: "12%",
            left: "5%",
            right: "5%",
            bottom: "10%",
            containLabel: true,
        },
        xAxis: {
            type: "category",
            data: series.map(item => item.name),
            axisLabel: {
                fontFamily: "Poppins",
                color: "#666",
                fontSize: 12,
            },
            axisTick: {
                show: false,
            },
            axisLine: {
                show: true,
                lineStyle: {
                    color: "#E0E0E0",
                },
            },
        },
        yAxis: {
            type: "value",
            max: yAxisMax,
            splitNumber: 6,
            splitLine: {
                lineStyle: {
                    type: "dashed",
                    color: "#E0E0E0",
                },
            },
            axisLabel: {
                fontFamily: "Poppins",
                color: "#666",
                fontSize: 12,
            },
            axisTick: {
                show: false,
            },
            axisLine: {
                show: false,
            },
        },
        series: [
            {
                type: "bar",
                data: series.map(item => ({
                    value: item.value,
                    itemStyle: {
                        color: item.color,
                        borderRadius: [15, 15, 0, 0],
                    },
                })),
                barWidth: "18%",
                emphasis: {
                    itemStyle: {
                        brightness: 0.1,
                    },
                },
            },
        ],
    };
}

export function getComponentForNormativeOptions(dataDash2) {
    if (
        !dataDash2 ||
        typeof dataDash2.component_active_for_normative === "undefined"
    ) {
        return {};
    }

    // Filter out items with count = 0 and sort by count descending
    const filteredData = dataDash2.component_active_for_normative
        .filter((item) => item.count > 0)
        .sort((a, b) => b.count - a.count)
        .map((item) => ({
            ...item,
            name: item.name.toUpperCase(), // Convert names to uppercase
        }));

    // Generate unique colors for each item
    const colors = [
        "#117ACB",
        "#0FBEF9",
        "#ff6b6b",
        "#4cc9f0",
        "#4895ef",
        "#560bad",
        "#f72585",
        "#7209b7",
        "#3a0ca3",
        "#3f37c9",
        "#117ACB",
        "#4895ef",
    ];

    return {
        tooltip: {
            trigger: "item",
            formatter: (params) => {
                return `${params.name.substring(0, 18)}${
                    params.name.length > 18 ? "..." : ""
                }<br/>Cantidad: ${params.value}`;
            },
            textStyle: {
                fontFamily: "Poppins, sans-serif"
            },
        },
        legend: {
            orient: "horizontal", // Cambio a horizontal
            bottom: "5%", // Posicionamiento en la parte inferior
            left: "center", // Centrado horizontalmente
            type: "scroll",
            width: "90%", // Mayor ancho para los nombres
            formatter: (name) => {
                const item = filteredData.find((d) => d.name === name);
                return name + " (" + item.count + ")";
            },
            textStyle: {
                fontSize: 12,
                fontFamily: "Poppins, sans-serif",
                color: "#333",
                overflow: "breakAll", // Permite el quiebre de texto
                width: 150, // Ancho máximo para cada elemento de la leyenda
            },
            icon: "circle",
            itemGap: 15, // Más espacio entre elementos
            itemWidth: 10, // Tamaño del círculo de color
            itemHeight: 10,
            pageTextStyle: {
                color: "#333",
            },
        },
        series: [
            {
                name: "Componentes",
                type: "pie",
                radius: ["40%", "70%"],
                center: ["50%", "45%"], // Ajustado para dar espacio a la leyenda
                avoidLabelOverlap: false,
                itemStyle: {
                    borderRadius: 6,
                    borderColor: "#fff",
                    borderWidth: 2,
                },
                label: {
                    show: true,
                    position: "inside",
                    formatter: "{c}",
                    fontSize: 14,
                    fontWeight: "bold",
                    color: "#fff",
                    textShadowColor: "rgba(0, 0, 0, 0.5)",
                    textShadowBlur: 3,
                    textShadowOffsetX: 1,
                    textShadowOffsetY: 1,
                },
                labelLine: {
                    show: false,
                },
                emphasis: {
                    scale: false,
                    itemStyle: {
                        shadowBlur: 10,
                        shadowOffsetX: 0,
                        shadowColor: "rgba(0, 0, 0, 0.5)",
                    },
                },
                data: filteredData.map((item, index) => ({
                    value: item.count,
                    name: item.name,
                    cas: item.cas, // Include the cas property for access in click event
                    itemStyle: {
                        color: colors[index % colors.length],
                    },
                })),
            },
        ],
        // Add click event handler 
        onClick: function(params) {
            if (params.data && params.data.cas) {
                // Return the cas property of the clicked item
                return params.data;
            }
        }
    };
}


/**
 * Función para obtener las opciones de configuración de un gráfico de Funcionalidad.
 *
 * @param {object} dataDash3
 * return {object} options
 */
export function getFunctionalityChartOptions(dataDash3) {
    // Validate input data
    if (
        !dataDash3 ||
        typeof dataDash3.sga_sensitizing_dermal_count === "undefined" ||
        typeof dataDash3.sga_sensitizing_respiratory_count === "undefined"
    ) {
        return {};
    }

    const categories = ["Cutánea", "Respiratorios"];
    const data = [
        dataDash3.sga_sensitizing_dermal_count || 0,
        dataDash3.sga_sensitizing_respiratory_count || 0,
    ];

    return {
        tooltip: {
            trigger: "axis",
            axisPointer: {
                type: "shadow",
            },
            formatter: "{b}: {c}",
        },
        legend: {
            data: ["Sensibilizantes"],
            textStyle: {
                fontFamily: "Poppins",
                color: "#333",
            },
            top: "5%",
            icon: "circle",
        },
        grid: {
            top: "12%",
            left: "5%",
            right: "5%",
            bottom: "10%",
            containLabel: true,
        },
        xAxis: {
            type: "category",
            data: categories,
            axisLabel: {
                fontFamily: "Poppins",
                color: "#666",
                fontSize: 12,
            },
            axisTick: {
                show: false,
            },
            axisLine: {
                show: true,
                lineStyle: {
                    color: "#E0E0E0",
                },
            },
        },
        yAxis: {
            type: "value",
            splitLine: {
                lineStyle: {
                    type: "dashed",
                    color: "#E0E0E0",
                },
            },
            axisLabel: {
                fontFamily: "Poppins",
                color: "#666",
                fontSize: 12,
            },
            axisTick: {
                show: false,
            },
            axisLine: {
                show: false,
            },
        },
        series: [
            {
                type: "bar",
                data: data.map((value, index) => ({
                    value: value,
                    itemStyle: {
                        color: getBarColor(index),
                        borderRadius: [15, 15, 0, 0],
                    },
                })),
                barWidth: "20%",
                emphasis: {
                    itemStyle: {
                        brightness: 0.1,
                    },
                },
            },
        ],
    };
}

/**
 * Opciones para el gráfico de Usabilidad (barras azules, datos quemados)
 */
export function getUsabilityChartOptions(items) {
  const categories = items.map(item => item.function_used);
  const data = items.map(item => item.total);

  return {
    tooltip: {
      trigger: "axis",
      axisPointer: { type: "shadow" },
      formatter: "{b}: {c}",
    },
    grid: {
      top: "12%",
      left: "5%",
      right: "5%",
      bottom: "10%",
      containLabel: true,
    },
    xAxis: {
      type: "category",
      data: categories,
      axisLabel: {
                fontFamily: "Poppins",
                color: "#666",
                fontSize: 13,
                interval: 0,
                rotate: 0,
                // Formatter que inserta saltos de línea en espacios para etiquetas largas
                formatter: function (value) {
                    if (!value) return value;
                    const stopwords = [
                        'de','la','el','los','las','del','y','a','para','por','con','sin','al','lo','en','un','una'
                    ];
                    const words = String(value)
                        .split(/\s+/)
                        .filter(w => !stopwords.includes(w.toLowerCase()));

                    const cleaned = words.join(' ');
                    const maxChars = 15; 
                    const maxLines = 2;
                    const parts = [];
                    let current = '';

                    cleaned.split(/\s+/).forEach((w) => {
                        if ((current + ' ' + w).trim().length <= maxChars) {
                            current = (current + ' ' + w).trim();
                        } else {
                            if (current) parts.push(current);
                            current = w;
                        }
                    });
                    if (current) parts.push(current);

                    if (parts.length > maxLines) {
                        const shown = parts.slice(0, maxLines - 1);
                        const rest = parts.slice(maxLines - 1).join(' ');
                        shown.push(rest.length > maxChars ? rest.substr(0, maxChars - 3) + '...' : rest);
                        return shown.join('\n');
                    }

                    return parts.join('\n');
                },
      },
      axisTick: { show: false },
      axisLine: {
        show: true,
        lineStyle: { color: "#E0E0E0" },
      },
    },
    yAxis: {
      type: "value",
      splitLine: {
        lineStyle: { type: "dashed", color: "#E0E0E0" },
      },
      axisLabel: {
        fontFamily: "Poppins",
        color: "#666",
        fontSize: 12,
      },
      axisTick: { show: false },
      axisLine: { show: false },
    },
    series: [
      {
        type: "bar",
        data: data.map((value) => ({
          value,
          itemStyle: {
            color: "#0CB1F7",
            borderRadius: [50, 50, 0, 0],
          },
        })),
        barWidth: "20%",
        emphasis: {
          itemStyle: { brightness: 0.1 },
        },
      },
    ],
  };
}